/* oxlint-disable no-restricted-imports */
/* oxlint-disable no-console -- log */

import { Title } from '@mantine/core'
import type { ProfileResource } from '@medplum/core'
import type {
  Questionnaire,
  QuestionnaireItem,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
} from '@medplum/fhirtypes'
import {
  Form,
  isQuestionEnabled,
  type QuestionnaireFormProps,
  useMedplum,
  useResource,
} from '@medplum/react'
import { buildInitialResponse } from '@seen/core/questionnaire'
import { createReference } from '@seen/core/references'
import { canonical } from '@seen/core/utils'
import { uniqueId } from 'lodash-es'
import { type JSX, useEffect, useState } from 'react'
import { QuestionnaireFormContext } from '#src/components/questionnaire-form/QuestionnaireFormContext.ts'
import { QuestionnairePageSequence } from '#src/components/questionnaire-form/QuestionnairePageSequence.tsx'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'

// =============================
// PATCH
// Adding our own CustomQuestionnaireFormProps type
// =============================
type CustomQuestionnaireFormProps = QuestionnaireFormProps & {
  /**
   * This prop must be stable across renders, otherwise the form will reset.
   */
  prefillResponseAnswers_STABLE?: QuestionnaireResponseItem[]
  onChange?: (response: QuestionnaireResponse) => void

  hideSubmitButton?: boolean
  submitting?: boolean
}

export function CustomQuestionnaireForm(
  props: CustomQuestionnaireFormProps
): JSX.Element | null {
  const isReadOnly = useIsReadOnly()
  const medplum = useMedplum()
  const source = medplum.getProfile()
  const [schemaLoaded, setSchemaLoaded] = useState(false)
  const questionnaire = useResource(props.questionnaire)
  const [response, setResponse] = useState<QuestionnaireResponse | undefined>()
  const [activePage, setActivePage] = useState(0)
  // =============================
  // PATCH
  // Adding a state to force a full reset of the QuestionnairePageSequence
  // when the prefillResponseAnswers_STABLE prop changes. we have to do this because
  // the input components store their own state and don't reset when their initial value changes.
  // the downside of this is that if you're editing the form when our initial value changes and
  // setPageSequenceKey is called, the form will be destroyed and re-rendered, losing the user's input
  // and focus state.
  // =============================
  const [pageSequenceKey, setPageSequenceKey] = useState<string | null>(null)

  useEffect(() => {
    medplum
      .requestSchema('Questionnaire')
      .then(() => medplum.requestSchema('QuestionnaireResponse'))
      .then(() => setSchemaLoaded(true))
      .catch(console.log)
  }, [medplum])

  useEffect(() => {
    if (questionnaire) {
      // =============================
      // PATCH
      // Using our prefilledQuestionnaireResponseAnswers prop to update the items
      // of the newly built initialResponse
      // =============================
      const initialResponse = buildInitialResponse(questionnaire)
      if (props.prefillResponseAnswers_STABLE) {
        const linkIdToAnswer = new Map<
          string,
          QuestionnaireResponseItem['answer']
        >()
        // Recursively add all answers to a map
        const addItemAnswerToMapRecursively = (
          item: QuestionnaireResponseItem
        ): void => {
          if (item.answer && item.answer.length > 0) {
            linkIdToAnswer.set(item.linkId, item.answer)
          } else if (item.item && item.item.length > 0) {
            item.item.forEach(addItemAnswerToMapRecursively)
          }
        }
        const setAnswerToItemRecursively = (
          item: QuestionnaireResponseItem
        ): void => {
          if (linkIdToAnswer.has(item.linkId)) {
            item.answer = linkIdToAnswer.get(item.linkId)
          }
          if (item.item !== undefined && item.item.length > 0) {
            item.item.forEach(setAnswerToItemRecursively)
          }
        }

        props.prefillResponseAnswers_STABLE.forEach((item) => {
          addItemAnswerToMapRecursively(item)
        })
        initialResponse.item?.forEach(setAnswerToItemRecursively)
        setPageSequenceKey(uniqueId())
      }
      setResponse(initialResponse)
    } else {
      setResponse(undefined)
    }
  }, [props.prefillResponseAnswers_STABLE, questionnaire])

  // =============================
  // PATCH
  // Moving return statement above some function declarations
  // =============================
  if (!schemaLoaded || !questionnaire || !response) {
    return null
  }

  function setItems(
    newResponseItems: QuestionnaireResponseItem | QuestionnaireResponseItem[]
  ): void {
    if (isReadOnly) {
      return
    }
    const currentItems = response?.item ?? []
    const mergedItems = mergeItems(
      currentItems,
      Array.isArray(newResponseItems) ? newResponseItems : [newResponseItems]
    )

    const newResponse: QuestionnaireResponse = {
      resourceType: 'QuestionnaireResponse',
      status: 'completed',
      item: mergedItems,
    }

    setResponse(newResponse)
    // =============================
    // PATCH
    // Using our own onChange prop to pass back the updated response
    // =============================
    if (props.onChange) {
      props.onChange(
        buildQuestionnaireResponse(
          newResponse,
          // we do a null check above
          questionnaire as Questionnaire,
          props.subject,
          source,
          'completed'
        )
      )
    }
  }

  function checkForQuestionEnabled(item: QuestionnaireItem): boolean {
    return item.type === 'group' || isQuestionEnabled(item, response)
  }

  // Medplum 4.3.4 deprecated much of the questionnaire form class implementation in favor of hooks.
  // We weren't using the paging functionality anyway, so we're just using a fixed number of pages.
  const numberOfPages = 1
  const nextStep = (): void => setActivePage((current) => current + 1)
  const prevStep = (): void => setActivePage((current) => current - 1)

  return (
    <QuestionnaireFormContext.Provider
      value={{ subject: props.subject, encounter: props.encounter }}
    >
      <Form
        testid="questionnaire-form"
        onSubmit={() => {
          if (props.onSubmit && response) {
            props.onSubmit(
              buildQuestionnaireResponse(
                response,
                questionnaire,
                props.subject,
                source,
                'completed'
              )
            )
          }
        }}
      >
        {questionnaire.title && <Title>{questionnaire.title}</Title>}
        <QuestionnairePageSequence
          key={pageSequenceKey}
          items={questionnaire.item ?? []}
          response={response}
          onChange={setItems}
          renderPages={numberOfPages > 1}
          activePage={activePage}
          numberOfPages={numberOfPages}
          submitting={props.submitting || isReadOnly}
          checkForQuestionEnabled={checkForQuestionEnabled}
          nextStep={nextStep}
          prevStep={prevStep}
          hideSubmitButton={props.hideSubmitButton}
        />
      </Form>
    </QuestionnaireFormContext.Provider>
  )
}

function mergeIndividualItems(
  prevItem: QuestionnaireResponseItem,
  newItem: QuestionnaireResponseItem
): QuestionnaireResponseItem {
  // Recursively merge the nested items based on their ids.
  const mergedNestedItems = mergeItems(prevItem.item ?? [], newItem.item ?? [])

  return {
    ...newItem,
    item: mergedNestedItems.length > 0 ? mergedNestedItems : undefined,
    answer:
      newItem.answer && newItem.answer.length > 0
        ? newItem.answer
        : prevItem.answer,
  }
}

function mergeItems(
  prevItems: QuestionnaireResponseItem[],
  newItems: QuestionnaireResponseItem[]
): QuestionnaireResponseItem[] {
  const result: QuestionnaireResponseItem[] = []
  const usedIds = new Set<string>()

  for (const prevItem of prevItems) {
    const itemId = prevItem.id
    const newItem = newItems.find((item) => item.id === itemId)

    if (newItem) {
      result.push(mergeIndividualItems(prevItem, newItem))
      usedIds.add(newItem.id as string)
    } else {
      result.push(prevItem)
    }
  }

  // Add items from newItems that were not in prevItems.
  for (const newItem of newItems) {
    if (!usedIds.has(newItem.id as string)) {
      result.push(newItem)
    }
  }

  return result
}

// =============================
// PATCH
// Adding buildQuestionnaireResponse function to build the QR object
// with the correct fields
// =============================
function buildQuestionnaireResponse(
  response: QuestionnaireResponse,
  questionnaire: Questionnaire,
  subject: QuestionnaireFormProps['subject'],
  source: ProfileResource | undefined,
  status: QuestionnaireResponse['status']
): QuestionnaireResponse {
  return {
    ...response,
    // =============================
    // PATCH
    // Using the canonical URL of the questionnaire instead a reference string
    // as this is the FHIR-compliant format for the questionnaire field on QR
    // =============================
    questionnaire: canonical(questionnaire),
    subject,
    source: createReference(source as ProfileResource),
    authored: new Date().toISOString(),
    status,
  }
}
